/* eslint-disable complexity */
import type { trackEcommerce } from '@svs/nova-core/nextjs/analytics';

import { ITS_PRODUCT_ID, type ITSProductId, productName, resolveITSProduct } from '../@types/Product.js';
import { logger } from '../utils/index.js';
import type { CancelContextApi } from './context/createCancelContextApi.js';

type Transaction = Parameters<typeof trackEcommerce>[0];
type TransactionItem = Transaction['items'][number];

const SPORTKRYSS_PRODUCT_ID = 30;
const addonProducts: Set<number | undefined> = new Set([SPORTKRYSS_PRODUCT_ID]);

const buildItem = ({
  itsProductId,
  price,
  isMarketplace,
}: {
  itsProductId: number | undefined;
  price: number;
  isMarketplace: boolean;
}): TransactionItem => {
  const knownProductName = itsProductId ? productName[itsProductId] : undefined;

  return {
    SKU: knownProductName ? `${knownProductName}.Annullering` : 'Okand.Annullering',
    productName: knownProductName ?? 'Okand',
    price,
    quantity: 1,
    optCategory: isMarketplace
      ? {
          shares: true,
          typeOfShares: 'c2c',
        }
      : undefined,
  };
};

const createTransaction = ({
  wagerId,
  totalAmount,
  items,
}: {
  wagerId?: string;
  totalAmount: number;
  items: TransactionItem[];
}): Transaction | undefined => {
  if (!wagerId) {
    return undefined;
  }

  const [firstItem, ...restItems] = items;
  if (!firstItem) {
    return undefined;
  }

  return {
    orderId: wagerId,
    totalAmount,
    items: [firstItem, ...restItems],
    optTaxAmount: 0,
    optShippingAmount: 0,
    optProductName: firstItem.productName,
  };
};

/**
 * Tracks the canceled wagers in Google Analytics.
 *
 * @param cancelWagerContext context api for the canceled wagers
 * @param trackTransaction optional callback for ecommerce transaction tracking
 */
export function trackCancelWager(cancelWagerContext: CancelContextApi, trackTransaction?: typeof trackEcommerce): void {
  const cancelItems = cancelWagerContext.getCancelItems();
  const wagers = cancelWagerContext.getWagers();
  for (const wager of wagers) {
    const response = cancelItems.get(wager)?.data;
    if (response) {
      const wagerId = response.serialNum ?? response.CancelWagerReply?.serial;
      let total = response.wagerAmounts && response.wagerAmounts.totalAmount;
      if (!total) {
        total = response.CancelWagerReply && response.CancelWagerReply.betAmount;
      }
      const totalAmount = Number.parseInt(total ?? '0', 10) * -1;

      const items: TransactionItem[] = [];

      const productId: ITSProductId | undefined = wager.isMarketplace
        ? wager.product
          ? ITS_PRODUCT_ID[wager.product]
          : undefined
        : response.products?.find((product) => !addonProducts.has(resolveITSProduct(product) ?? undefined));

      const itsProductId: number | undefined = resolveITSProduct(productId);
      items.push(
        buildItem({
          itsProductId,
          price: wager.price * -1,
          isMarketplace: wager.isMarketplace,
        })
      );

      if (response.products && response.products.length > 1) {
        const addonProductId = response.products.find((product) =>
          addonProducts.has(resolveITSProduct(product) ?? undefined)
        );
        const addonItsProductId: number | undefined = resolveITSProduct(addonProductId);
        items.push(
          buildItem({
            itsProductId: addonItsProductId,
            price: (wager.addonPrice ?? 0) * -1,
            isMarketplace: false,
          })
        );
      }

      const trackingCancelWager = createTransaction({
        wagerId,
        totalAmount,
        items,
      });

      if (!trackingCancelWager) {
        logger.warn('Skipping ecommerce cancel tracking due to missing tracking payload', {
          wagerId,
        });
        continue;
      }

      if (trackTransaction) {
        trackTransaction(trackingCancelWager);
        continue;
      }

      logger.info('No trackTransaction function provided, unable to track transaction event for canceled wager', {
        wagerId,
      });
    }
  }
}
